import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  Grid,
  TextField,
  Typography,
  IconButton,
} from "@mui/material";
import { Tag } from "../../model/Tag";
import { useFormikContext } from "formik";
import DeleteIcon from "@mui/icons-material/Delete";

interface TagEditorLineProps {
  tag: Tag;
  fieldNamePrefix: string;
  isLast: boolean;
  index: number;
  handleDelete: (index: number) => void;
}

const TagEditorLine = ({
  tag,
  fieldNamePrefix,
  isLast,
  index,
  handleDelete,
}: TagEditorLineProps) => {
  const { values, setFieldValue } = useFormikContext<{ tags: Tag[] }>();

  const handleChange = (field: string, value: string) => {
    // The fieldNamePrefix already contains the full path to the tag in the form
    // So we just need to append the field name (nom or color)
    // If this is the last row and we're entering something, add a new empty row
    if (isLast && value.trim() !== "") {
      // Add a new empty tag at the end of the array
      const newTag = {};
      setFieldValue("tags", [...values.tags, newTag]);
    }

    setFieldValue(`${fieldNamePrefix}.${field}`, value);
  };

  return (
    <>
      <Grid size={3} sx={{ display: "flex", alignItems: "center" }}>
        {tag.nom && (
          <Chip
            label={tag.nom || "New tag"}
            sx={{
              backgroundColor: tag.color || "#e0e0e0",
              marginTop: "15px",
              height: "32px",
              "& .MuiChip-label": {
                fontWeight: 500,
                paddingLeft: "12px",
                paddingRight: "12px",
              },
            }}
          />
        )}
      </Grid>
      <Grid size={3}>
        <TextField
          name={`${fieldNamePrefix}.nom`}
          key={tag.id}
          label="Nom"
          variant="standard"
          fullWidth
          value={tag.nom || ""}
          onChange={(e) => handleChange("nom", e.target.value)}
        />
      </Grid>
      <Grid size={2}>
        <FormControl variant="standard" fullWidth>
          <InputLabel htmlFor={`color-input-${tag.id || "new"}`} shrink>
            Couleur
          </InputLabel>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mt: 2,
              pb: 0.5,
              borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
              "&:hover": {
                borderBottom: "2px solid rgba(0, 0, 0, 0.87)",
                pb: "calc(0.5rem - 1px)",
              },
              "&:focus-within": {
                borderBottom: "2px solid #1976d2",
                pb: "calc(0.5rem - 1px)",
              },
            }}
          >
            <input
              id={`color-input-${tag.id || "new"}`}
              type="color"
              value={tag.color || "#e0e0e0"}
              onChange={(e) => handleChange("color", e.target.value)}
              style={{
                width: "28px",
                height: "28px",
                padding: "0",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                marginRight: "12px",
              }}
            />
            <Typography
              variant="body1"
              sx={{
                color: "rgba(0, 0, 0, 0.87)",
                fontSize: "1rem",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                lineHeight: "1.5",
                letterSpacing: "0.00938em",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {tag.color || "#e0e0e0"}
            </Typography>
          </Box>
        </FormControl>
      </Grid>
      <Grid size={1} sx={{ display: "flex", alignItems: "center" }}>
        <IconButton
          sx={{ marginTop: "15px" }}
          onClick={() => handleDelete(index)}
        >
          <DeleteIcon />
        </IconButton>
      </Grid>
      <Grid size={3} />
    </>
  );
};

export default TagEditorLine;
